import { useEffect, useState } from "react";
import FormField from "./FormField";
import Container from "./Container";
import { InputText } from "primereact/inputtext";
import useAuthHttp from "../hooks/useAuthHttp";
import { useQuery } from "@tanstack/react-query";
import Icon from "./Icon";
import Text from "./Text";
import { ProgressSpinner } from "primereact/progressspinner";

/* eslint-disable @typescript-eslint/no-explicit-any */
export interface ITreasureBoundsSearchResult {
    treasure_list: ITreasureList[]
}

export interface ITreasureSearchResponse {
    date_open:         string;
    date_close:        string;
    anul_red_rate:     number;
    untr_red_value:    number;
    anul_invstmt_rate: number;
    untr_invstmt_val:  number;
}
  
export interface ITreasureList {
    id: string
    TrsrBd: ITrsrBd|null
    TrsrBdType: ITrsrBdType|null
    SelicCode?: number
    PrcgLst: any[]
}
  
export interface ITrsrBd {
    cd: number
    nm: string
    featrs: string
    mtrtyDt: string
    minInvstmtAmt: number
    untrInvstmtVal: number
    invstmtStbl?: string
    semiAnulIntrstInd: boolean
    rcvgIncm?: string
    anulInvstmtRate: number
    anulRedRate: number
    minRedQty: number
    untrRedVal: number
    minRedVal: number
    isinCd: string
    FinIndxs: IFinIndxs
    wdwlDt: any
    convDt?: string
    BusSegmt?: IBusSegmt
    amortQuotQty?: number
}
  
export interface IFinIndxs {
    cd: number
    nm: string
}
  
export interface IBusSegmt {
    cd: number
    nm: string
}
  
export interface ITrsrBdType {
    cd: number
    nm: string
    ctdyRate?: number
    grPr?: number
}

export interface ITreasureBondsSearchSelectorProps {
    hasError?: boolean;
    error?: string;
    selected?: ITreasureList|undefined;
    onSelect: (selected: ITreasureList|null) => void;
}

const TreasureBondsSearchSelector = ({ hasError, error, selected, onSelect }: ITreasureBondsSearchSelectorProps) => {
    const [searchQuery, setSearchQuery] = useState("");
    const authHttp = useAuthHttp();
    const treasureBoundsQuery = useQuery({
        queryKey: ["banks", searchQuery],
        queryFn: async () => {
            if (searchQuery.length < 3) {
                return null;
            }

            return await authHttp.doGet<ITreasureBoundsSearchResult>(`/treasure/br/list?search=${encodeURIComponent(searchQuery)}`);
        }
    });

    const [searchQueryTimeouted, setSearchQueryTimeouted] = useState<string>("");

    useEffect(() => {
        const timeout = setTimeout(() => {
            setSearchQuery(searchQueryTimeouted);
        }, 1500);

        return () => clearTimeout(timeout);
    }, [searchQueryTimeouted]);

    return (
        <FormField
            id="treasureBoundsSearchSelector"
            label="Procure pelo título"
            hasError={hasError}
            error={error}
        >
            <Container relative>
                <InputText 
                    value={selected?.TrsrBd?.nm ?? searchQueryTimeouted}
                    className="w-full pr-5"
                    invalid={hasError}
                    onChange={(e) => {
                        setSearchQueryTimeouted(e.target.value);
                    }}
                />
                
                {
                    selected && <Icon 
                        name="pi-times"
                        className="absolute right-0 mr-1 p-2 text-red-500 bg-white"
                        onClick={() => {
                            onSelect(null);
                            setSearchQueryTimeouted("");
                        }}
                        style={{ transform: "translateY(-50%)", top: "50%" }}
                    />
                }
            </Container>
            {
                treasureBoundsQuery.isFetching && (
                    <Container center flexColumn className="my-progress-spinner p-4" gap={2}>
                        <ProgressSpinner className="w-2rem h-auto" />
                        <Text>Carregando...</Text>
                    </Container>       
                )
            }

            {
                treasureBoundsQuery.data?.treasure_list?.length && !selected && searchQueryTimeouted && <div className="bg-white w-full">
                    <ul className="list-none p-0 flex gap-2 flex-column">
                        {
                            treasureBoundsQuery.data?.treasure_list.map(f => {
                                return (
                                    <li key={f.id}>
                                        <a 
                                            className="cursor-pointer" 
                                            onClick={() => {
                                                onSelect(f);
                                            }}
                                        >
                                            {f.TrsrBd?.nm}
                                        </a>
                                    </li>
                                );
                            })
                        }
                    </ul>
                </div>
            }
        </FormField>
    );
}

export default TreasureBondsSearchSelector;
